const CART_KEYS = {
  bookings: "bookings:v1",
  checkups: "checkups:v1",
  passes: "passes:v1",
};

const LEGACY_CART_KEYS = {
  bookings: "bookings",
  checkups: "checkups",
  passes: "passes",
};

function parseStoredArray(rawValue) {
  if (!rawValue) return [];

  try {
    const parsed = JSON.parse(rawValue);
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    console.error("Failed to parse stored cart data:", error);
    return [];
  }
}

function migrateLegacyCart(key) {
  const versionedKey = CART_KEYS[key];
  const legacyKey = LEGACY_CART_KEYS[key];

  const existingVersioned = parseStoredArray(window.localStorage.getItem(versionedKey));
  if (existingVersioned.length > 0) {
    return existingVersioned;
  }

  const legacyValue = parseStoredArray(window.localStorage.getItem(legacyKey));
  if (legacyValue.length > 0) {
    window.localStorage.setItem(versionedKey, JSON.stringify(legacyValue));
    window.localStorage.removeItem(legacyKey);
  }

  return legacyValue;
}

export function readCartItems(key) {
  return migrateLegacyCart(key);
}

export function writeCartItems(key, items) {
  window.localStorage.setItem(CART_KEYS[key], JSON.stringify(items));
  window.dispatchEvent(new Event("cartUpdated"));
}

export function removeCartItems(key) {
  window.localStorage.removeItem(CART_KEYS[key]);
  window.localStorage.removeItem(LEGACY_CART_KEYS[key]);
  window.dispatchEvent(new Event("cartUpdated"));
}

/**
 * Subscribe to anything that may invalidate the in-memory cart state:
 *
 *   - "cartUpdated" — fired by writeCartItems / removeCartItems in this tab.
 *   - "storage"     — fired by the browser when another tab mutates localStorage.
 *   - "pageshow" with `event.persisted === true` — fired when the page is
 *     restored from the back/forward cache (bfcache). Without this, pressing
 *     the browser Back button after a payment-clear leaves stale React state
 *     showing items that no longer exist in localStorage.
 *
 * The callback is invoked synchronously on every relevant event; consumers
 * should re-read cart items via `readCartItems`.
 *
 * Returns a cleanup function that removes all listeners.
 */
export function subscribeToCartChanges(callback) {
  const onCartUpdated = () => callback();
  const onStorage = (event) => {
    if (!event || !event.key) {
      callback();
      return;
    }
    const watchedKeys = new Set([
      ...Object.values(CART_KEYS),
      ...Object.values(LEGACY_CART_KEYS),
    ]);
    if (watchedKeys.has(event.key)) callback();
  };
  const onPageShow = (event) => {
    if (event && event.persisted) callback();
  };

  window.addEventListener("cartUpdated", onCartUpdated);
  window.addEventListener("storage", onStorage);
  window.addEventListener("pageshow", onPageShow);

  return () => {
    window.removeEventListener("cartUpdated", onCartUpdated);
    window.removeEventListener("storage", onStorage);
    window.removeEventListener("pageshow", onPageShow);
  };
}
